import { Printer } from "@redotech/redo-fulfillment-group-common/printer";
import { useStreamingEnabled } from "@redotech/redo-merchant-app-common/streaming-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { CloseStream, useWatchStream } from "../hooks/use-watch-stream";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export interface PrinterContextValue {
  printers: Printer[];
  printerIdIndex: Record<number, number>;
}

export const PrinterContext = createContext<PrinterContextValue>({
  printers: [],
  printerIdIndex: {},
});

export const PrinterProvider = memo(function PrintersProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [printerIdIndex, setPrinterIdIndex] = useState<Record<number, number>>(
    {},
  );

  const client = useContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const isOmsEnabled = team?.settings.outboundLabels?.newOmsEnabled;
  const streamingEnabled = useStreamingEnabled();

  useEffect(() => {
    if (!client || !isOmsEnabled || !streamingEnabled) {
      return;
    }

    void client.refreshPrintNodeIntegration({});
    const interval = setInterval(
      () => {
        void client.refreshPrintNodeIntegration({});
      },
      Temporal.Duration.from({ minutes: 10 }).total("millisecond"),
    );

    return () => clearInterval(interval);
  }, [client, isOmsEnabled, streamingEnabled]);

  useWatchStream(
    isOmsEnabled && streamingEnabled ? client : undefined,
    "watchPrinters",
    {},
    ({ printers = [], notEnabled }) => {
      if (notEnabled) {
        throw new CloseStream();
      }

      const sortedPrinters = printers.sort(
        (a, b) => Number(a.state === "online") - Number(b.state === "online"),
      );

      setPrinters(sortedPrinters);
      setPrinterIdIndex(
        sortedPrinters.reduce(
          (acc, printer, index) => ({ ...acc, [printer.id]: index }),
          {} as Record<number, number>,
        ),
      );
    },
    true,
  );

  return (
    <PrinterContext.Provider value={{ printers, printerIdIndex }}>
      {children}
    </PrinterContext.Provider>
  );
});
